// ModalPagoManualWifi.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./ModalPagoManual.css";

function pad2(n) {
  return String(n).padStart(2, "0");
}
function toSqlDatetimeFromDateOnly(dateStr) {
  const now = new Date();
  return `${dateStr} ${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`;
}

export default function ModalPagoManualWifi({ cliente, onClose, onSubmit, submitting }) {
  const clienteNombre = useMemo(() => {
    const n = `${cliente?.nombre || ""} ${cliente?.apellidos || ""}`.trim();
    return n || `Cliente #${cliente?.id ?? "-"}`;
  }, [cliente]);

  const [monto, setMonto] = useState("");
  const [forma, setForma] = useState("oficina"); // oficina|bancomer|banamex
  const [concepto, setConcepto] = useState("dias_restantes");
  const [referencia, setReferencia] = useState("");
  const [fecha, setFecha] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  });

  // ✅ nuevo: modalidad y meses para adeudos
  const [modalidad, setModalidad] = useState("contado"); // contado | meses
  const [mesesAdeudo, setMesesAdeudo] = useState(1); // 1..6

  const [error, setError] = useState("");

  const esAdeudoEspecial = concepto === "modem_extra" || concepto === "cambio_domicilio";

  useEffect(() => setError(""), [monto, forma, concepto, referencia, fecha, modalidad, mesesAdeudo]);

  // Si cambias concepto, resetea modalidad/meses cuando aplique
  useEffect(() => {
    if (!esAdeudoEspecial) {
      setModalidad("contado");
      setMesesAdeudo(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concepto]);

  const submit = () => {
    setError("");

    if (!cliente?.id) return setError("Cliente inválido");
    if (!fecha) return setError("Selecciona una fecha");

    // ✅ Permitir monto 0, pero NO permitir vacío ni negativos
    const rawMonto = String(monto).trim();
    if (rawMonto === "") return setError("Monto requerido");

    const m = Number(rawMonto);
    if (!Number.isFinite(m) || m < 0) return setError("Monto inválido");

    const [yyyy, mm] = fecha.split("-").map(Number);
    if (!yyyy || !mm) return setError("Fecha inválida");

    // ✅ Validación meses si aplica
    if (esAdeudoEspecial && modalidad === "meses") {
      const ms = Number(mesesAdeudo);
      if (!Number.isFinite(ms) || ms < 1 || ms > 6) return setError("Meses inválidos (1 a 6)");
    }

    const payload = {
      cliente_id: cliente.id,
      monto: m,
      forma_pago: forma,
      fecha_pago_hora: toSqlDatetimeFromDateOnly(fecha),

      concepto,

      // para pagos (dias_restantes)
      mes: mm,
      anio: yyyy,
      referencia: referencia?.trim() || null,

      // ✅ para adeudos
      modalidad, // contado | meses
      meses_adeudo: esAdeudoEspecial && modalidad === "meses" ? Number(mesesAdeudo) : null,

      // para ticket pagos
      _ticketPagos: [{ mes: mm, anio: yyyy }],
    };

    onSubmit?.(payload);
  };

  return (
    <div
      className="ios-sheet-overlay"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target.classList.contains("ios-sheet-overlay")) onClose?.();
      }}
    >
      <div className="ios-sheet" onMouseDown={(e) => e.stopPropagation()}>
        <div className="ios-sheet-handle" />

        <div className="ios-sheet-head">
          <div>
            <div className="ios-sheet-title">Registrar pago</div>
            <div className="ios-sheet-sub">
              {clienteNombre} · #{cliente?.id}
            </div>
          </div>
          <button className="ios-sheet-close" onClick={onClose} disabled={submitting} type="button">
            ✕
          </button>
        </div>

        <div className="ios-sheet-card">
          <div className="ios-row">
            <label>Monto</label>
            <div className="ios-inputwrap">
              <span className="ios-prefix">$</span>
              <input
                inputMode="decimal"
                placeholder="0.00"
                value={monto}
                onChange={(e) => {
                  // Mantén solo números y un punto decimal
                  const v = e.target.value.replace(/[^0-9.]/g, "");
                  const parts = v.split(".");
                  const normalized =
                    parts.length <= 2 ? v : parts[0] + "." + parts.slice(1).join("");
                  setMonto(normalized);
                }}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="ios-row">
            <label>Concepto</label>
            <select
              className="ios-text"
              value={concepto}
              onChange={(e) => setConcepto(e.target.value)}
              disabled={submitting}
            >
              <option value="dias_restantes">Días restantes</option>
              <option value="cable_extra">Cable extra</option>
              <option value="modem_extra">Módem extra</option>
              <option value="reparacion">Reparación</option>

              {/* tus nuevos */}
              <option value="adeudo">Adeudo</option>
              <option value="cambio_domicilio">Cambio de domicilio</option>
            </select>
          </div>

          {/* ✅ Solo para módem extra y cambio de domicilio */}
          {esAdeudoEspecial && (
            <>
              <div className="ios-row">
                <label>Modalidad</label>
                <select
                  className="ios-text"
                  value={modalidad}
                  onChange={(e) => setModalidad(e.target.value)}
                  disabled={submitting}
                >
                  <option value="contado">Contado</option>
                  <option value="meses">A meses</option>
                </select>
              </div>

              {/* ✅ si es meses: 1..6 */}
              {modalidad === "meses" && (
                <div className="ios-row">
                  <label>Meses</label>
                  <select
                    className="ios-text"
                    value={mesesAdeudo}
                    onChange={(e) => setMesesAdeudo(Number(e.target.value))}
                    disabled={submitting}
                  >
                    {[1, 2, 3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>
                        {n} mes(es)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          <div className="ios-row">
            <label>Forma de pago</label>
            <div className="ios-seg">
              {[
                ["oficina", "Oficina"],
                ["banamex", "Banamex"],
                ["bancomer", "Bancomer"],
              ].map(([val, txt]) => (
                <button
                  key={val}
                  type="button"
                  className={`ios-seg-btn ${forma === val ? "active" : ""}`}
                  onClick={() => setForma(val)}
                  disabled={submitting}
                >
                  {txt}
                </button>
              ))}
            </div>
          </div>

          <div className="ios-row">
            <label>Fecha</label>
            <input
              className="ios-date"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              disabled={submitting}
            />
          </div>

          {/* referencia solo aplica para dias_restantes (si quieres) */}
          {concepto === "dias_restantes" && (
            <div className="ios-row">
              <label>Referencia (opcional)</label>
              <input
                className="ios-text"
                placeholder="Ej. nota..."
                value={referencia}
                onChange={(e) => setReferencia(e.target.value)}
                disabled={submitting}
              />
            </div>
          )}

          {error ? <div className="ios-error">{error}</div> : null}
        </div>

        <div className="ios-sheet-actions">
          <button className="ios-btn ghost" onClick={onClose} disabled={submitting} type="button">
            Cancelar
          </button>
          <button className="ios-btn primary" onClick={submit} disabled={submitting} type="button">
            {submitting ? "Registrando..." : "Registrar"}
          </button>
        </div>
      </div>
    </div>
  );
}

